import { Modal } from "react-bootstrap"
import Messages from "./Messages.js"
import * as CommonUtilities from "../utils/CommonUtils.js"
import * as CartMigration from "../utils/CartMigration.js"

/* =============================================================================
   The store-change modal.

   ⚠ ITS PURPOSE CHANGED (8 Oct). It was "warn before destroying the basket";
   it is now "report what changes". Spec: S8MC Specs\Cart Location Change\.

   It still falls back to the old destroy-warning for the two cases where
   clearing really is the whole answer — see `Unknown` and the all-removed
   branch below. Those are not leftovers; they are the honest answer when we
   cannot promise what survives.

   ⚠ The item names here come from the server. They render as React children,
   so they are escaped — do not switch any of this to dangerouslySetInnerHTML.
   ============================================================================= */

function money(value) {
    return CommonUtilities.StaticFields.CurrencySymbol + Number(value).toFixed(2);
}

export default function AlertModal(props) {
    const change = props.cartChange || {};
    const removed = change.Removed || [];
    const repriced = change.Repriced || [];
    const carried = change.Carried || [];

    const nothingSurvives = removed.length > 0 && carried.length === 0 && repriced.length === 0;

    let body;

    if (change.Checking) {
        // The new store's menu is a network call between the tap and the answer.
        body = <div className="wo-chg__checking">
                   <span className="spinner-border spinner-border-sm me-2" aria-hidden="true"></span>
                   {Messages.CartMigrationChecking}
               </div>;

    } else if (change.Unknown) {
        // Could not read a menu, so we cannot say what survives. Fall back to
        // the old warning rather than make a promise we cannot keep.
        body = Messages.LocationWarnChange;

    } else if (nothingSurvives) {
        body = Messages.CartMigrationAllRemoved;

    } else if (removed.length === 0 && repriced.length === 0) {
        body = <div>
                   {Messages.CartMigrationNoChange}
                   {/* Even when every item moves cleanly, a voucher does not. */}
                   {change.HasVoucher && <p className="wo-chg__tail mt-2">{Messages.CartMigrationVoucher}</p>}
               </div>;

    } else {
        /* Removed is split by reason: "not sold here" is permanent, "not
           available right now" is today's sold-out. Telling a customer their
           favourite is not sold at a store that simply ran out is wrong. */
        const notSold = removed.filter((r) => r.reason !== CartMigration.REMOVED_SOLD_OUT);
        const soldOut = removed.filter((r) => r.reason === CartMigration.REMOVED_SOLD_OUT);

        body = <div className="wo-chg">
            {notSold.length > 0 &&
                <div className="wo-chg__group">
                    <p className="wo-chg__head">{Messages.CartMigrationRemovedHead}</p>
                    <ul className="wo-chg__list">
                        {notSold.map((r) => (
                            <li key={r.line.recID}>
                                {r.line.productName}
                                {/* Name the part or modifier that caused it — "your
                                    combo is unavailable" with no reason invites a
                                    support call. */}
                                {r.detail && <span className="wo-chg__why"> — no {r.detail}</span>}
                            </li>
                        ))}
                    </ul>
                </div>}

            {soldOut.length > 0 &&
                <div className="wo-chg__group">
                    <p className="wo-chg__head">{Messages.CartMigrationSoldOutHead}</p>
                    <ul className="wo-chg__list">
                        {soldOut.map((r) => <li key={r.line.recID}>{r.line.productName}</li>)}
                    </ul>
                </div>}

            {repriced.length > 0 &&
                <div className="wo-chg__group">
                    <p className="wo-chg__head">{Messages.CartMigrationRepricedHead}</p>
                    <ul className="wo-chg__list">
                        {repriced.map((r) => (
                            <li key={r.line.recID}>
                                {r.line.productName}
                                <span className="wo-chg__price">
                                    <s>{money(r.oldPrice)}</s> &rarr; <b>{money(r.newPrice)}</b>
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>}

            {(carried.length > 0 || repriced.length > 0) &&
                <p className="wo-chg__tail">{Messages.CartMigrationKeptTail}</p>}

            {change.HasVoucher &&
                <p className="wo-chg__tail mt-2">{Messages.CartMigrationVoucher}</p>}
        </div>;
    }

    /* Both buttons are disabled while checking or rebuilding. ⚠ Confirming
       mid-check would commit to a change nobody has been shown. */
    const working = change.Checking === true || change.Busy === true;

    return (
        <Modal show={props.alertModal.ShowAlertModal} backdrop="static" keyboard={false}>
            <Modal.Body>
                <div className="row col-12 justify-content-center">
                    <div className="col-10 text-center">
                        {body}
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <div className="col-12 d-flex justify-content-sm-between justify-content-center flex-column flex-sm-row">
                    <button type="button" className="btn btn-outline-dark rounded-pill px-5 my-1 my-sm-0 mx-auto" onClick={props.handleCancelChangeLocation} disabled={change.Busy === true}>Cancel change</button>
                    <button type="button" className="btn btn-dark rounded-pill px-5 my-1 my-sm-0 mx-auto" onClick={props.handleConfirmChangeLocation} disabled={working}>
                        Change location
                        {change.Busy && <>&ensp;<span className="spinner-border spinner-border-sm" aria-hidden="true"></span></>}
                    </button>
                </div>
            </Modal.Footer>
        </Modal>
    )
}
